import React, { useContext, useState, FC, useEffect } from "react";
import { useRouter } from "next/router";
import { ApolloError } from "@apollo/client";
import { GraphQLError } from "graphql";
import { ModalContext } from "context/modal";
import { UserContext } from "context/user";
import { LoginForm as PepLoginForm } from "@pepdirect/v3/ui/LoginForm";
import { logEvent } from "@pepdirect/v3/helpers/analyticsLogger";
import { endpoints } from "config";
import { parseLoginUserGraphQLErrors } from "@pepdirect/v3/helpers/errors";
import { EVENT_NAMES } from "@pepdirect/v3/types";
import {
  useLoginMutation,
  LoginMutationVariables,
} from "services/graphql/generated";
import { AppContext } from "context/app";
import { isTenantReadOnly } from "@pepdirect/v3/helpers/tenant";

interface LoginFormProps {
  inModal?: boolean;
}

export const LoginForm: FC<LoginFormProps> = ({ inModal = false }) => {
  const { tenant } = useContext(AppContext);
  const { open, close } = useContext(ModalContext);
  const { setUser } = useContext(UserContext);

  const [mappedErrorMessage, setMappedErrorMessage] = useState("");
  const [showUpdatePasswordScreen, setShowUpdatePasswordScreen] =
    useState(false);

  const [loginMutation, { loading }] = useLoginMutation();
  const router = useRouter();
  const readOnly = isTenantReadOnly(tenant);

  useEffect(() => {
    !readOnly && router.prefetch("/home");
  }, [readOnly, router]);

  const onSubmit = async ({ email, password }: LoginMutationVariables) => {
    setMappedErrorMessage("");
    try {
      const { data } = await loginMutation({
        variables: { email, password },
      });
      logEvent(data?.loginUser?.id, EVENT_NAMES.landingPageLogin);
      setUser(data?.loginUser?.id);
      router.push(
        readOnly ? `${endpoints.checkout}/v3/dashboard/orders` : "/home"
      );
      close();
    } catch (e) {
      if (e instanceof ApolloError && e.graphQLErrors.length) {
        const { errorMsg, passwordResetRequired } = parseLoginUserGraphQLErrors(
          e.graphQLErrors as GraphQLError[]
        );

        if (passwordResetRequired) {
          setShowUpdatePasswordScreen(true);
        } else {
          setMappedErrorMessage(errorMsg);
        }
      }
    }
  };

  const goToForgotPassword = () => {
    if (inModal) {
      open("FORGOT_PASSWORD_MODAL");
    } else {
      close();
      router.push("/forgot-password");
    }
  };

  const goToShop = () => {
    if (inModal) {
      close();
    } else {
      router.push("/");
    }
  };

  return (
    <PepLoginForm
      customTexts={tenant?.brandConfig.customTexts}
      error={mappedErrorMessage || ""}
      goToForgotPassword={goToForgotPassword}
      inModal={inModal}
      isLoading={loading}
      logoSrc={`${endpoints.cdn}/static-images/pepsico-shop-icons/logo-blue.svg`}
      goToShop={goToShop}
      onSubmit={onSubmit}
      showUpdatePasswordScreen={showUpdatePasswordScreen}
      tenantName={tenant?.brandConfig.title}
    />
  );
};
